/**
 * Our story page: three small scroll and tap behaviours.
 *
 * <story-hero>        drifts the photo slower than the page (parallax) and
 *                     scrolls to the next section from the "read" cue.
 * <scroll-manifesto>  lights the story word by word as the section scrolls
 *                     through the viewport; the text sits pinned meanwhile.
 * <brew-process>      a stepper: tabs with a progress rail, arrow keys, swipe
 *                     on the panel, and an optional slow auto-advance that
 *                     runs only while the section is on screen and stops
 *                     for good once the shopper takes over.
 *
 * Without JavaScript, or with reduced motion, everything renders in its
 * final state: the whole story lit and every brewing step readable.
 */
import { getScrollEventTarget } from '@theme/scroll-container';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** One rAF-throttled scroll/resize subscription shared by every element. */
const scrollSubscribers = new Set();
let frame = 0;
function onScroll() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    scrollSubscribers.forEach((fn) => fn());
  });
}
let listening = false;
function subscribe(fn) {
  scrollSubscribers.add(fn);
  if (!listening) {
    listening = true;
    getScrollEventTarget().addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }
  fn();
}
function unsubscribe(fn) {
  scrollSubscribers.delete(fn);
}

class StoryHero extends HTMLElement {
  #update = () => {
    const rect = this.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    // The photo moves at 70% of the page speed, so it drifts behind the text.
    const shift = Math.max(0, -rect.top) * 0.3;
    this.style.setProperty('--story-hero-shift', `${shift.toFixed(1)}px`);
  };

  connectedCallback() {
    this.querySelector('[data-story-next]')?.addEventListener('click', this.#next);
    if (!REDUCED_MOTION.matches) subscribe(this.#update);
  }

  disconnectedCallback() {
    unsubscribe(this.#update);
  }

  #next = (event) => {
    const section = this.closest('.shopify-section');
    const next = section?.nextElementSibling;
    if (!next) return;
    event.preventDefault();
    next.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
  };
}

class ScrollManifesto extends HTMLElement {
  /** @type {HTMLElement[]} */
  #words = [];
  #lit = -1;

  #update = () => {
    const rect = this.getBoundingClientRect();
    const travel = rect.height - window.innerHeight;
    // 0 when the section's top reaches the top of the viewport, 1 when its end does.
    // Starting a little early (0.35 of a screen) so the first words light as it arrives.
    const start = window.innerHeight * 0.35;
    const progress = travel > 0 ? (start - rect.top) / (travel + start) : rect.top < start ? 1 : 0;
    const count = Math.round(Math.min(1, Math.max(0, progress)) * this.#words.length * 1.08);
    if (count === this.#lit) return;
    this.#lit = count;
    this.#words.forEach((word, index) => word.classList.toggle('is-lit', index < count));
  };

  connectedCallback() {
    this.#words = Array.from(this.querySelectorAll('.manifesto__word'));
    if (REDUCED_MOTION.matches || this.#words.length === 0) return;
    this.dataset.ready = '';
    subscribe(this.#update);
  }

  disconnectedCallback() {
    unsubscribe(this.#update);
  }
}

class BrewProcess extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  #index = 0;
  #auto = false;
  #visible = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #touchX = 0;

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.#tabs.length === 0) return;

    this.dataset.ready = '';
    this.#tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index, true));
      tab.addEventListener('keydown', (event) => this.#onKey(event, index));
    });

    const panelsWrap = this.querySelector('.brew__panels');
    panelsWrap?.addEventListener('touchstart', (e) => { this.#touchX = e.touches[0].clientX; }, { passive: true });
    panelsWrap?.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - this.#touchX;
      if (Math.abs(dx) > 40) this.#select(this.#index + (dx < 0 ? 1 : -1), true);
    });

    // The rail on the active tab fills over the interval; when it finishes, move on.
    this.addEventListener('animationend', (event) => {
      if (event.animationName === 'brew-fill' && this.#auto) this.#select(this.#index + 1, false);
    });

    this.#auto = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = !!entry?.isIntersecting;
      this.#syncPlaying();
    }, { threshold: 0.4 });
    this.#observer.observe(this);

    // Hovering or focusing inside pauses the auto-advance without cancelling it.
    this.addEventListener('pointerenter', () => this.toggleAttribute('data-paused', true));
    this.addEventListener('pointerleave', () => this.toggleAttribute('data-paused', false));
    this.addEventListener('focusin', () => this.toggleAttribute('data-paused', true));
    this.addEventListener('focusout', () => this.toggleAttribute('data-paused', false));

    // Theme editor: selecting a step in the sidebar shows it
    this.addEventListener('shopify:block:select', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      const index = tab ? this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab)) : -1;
      if (index >= 0) this.#select(index, true);
    });

    this.#select(0, false);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #syncPlaying() {
    this.toggleAttribute('data-playing', this.#auto && this.#visible);
  }

  /** @param {number} index @param {boolean} byUser */
  #select(index, byUser) {
    const count = this.#tabs.length;
    this.#index = ((index % count) + count) % count;
    if (byUser) this.#auto = false; // the shopper is driving now
    this.#syncPlaying();
    this.#tabs.forEach((tab, i) => {
      const active = i === this.#index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle('is-done', i < this.#index);
      // Restart the fill animation on the newly active tab.
      const fill = tab.querySelector('.brew__fill');
      if (fill && active) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
    });
    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== this.#index;
    });
    this.style.setProperty('--brew-progress', String((this.#index + 1) / count));
  }

  /** @param {KeyboardEvent} event @param {number} index */
  #onKey(event, index) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (event.key in keys) {
      event.preventDefault();
      this.#select(index + keys[event.key], true);
      this.#tabs[this.#index]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.#select(event.key === 'Home' ? 0 : this.#tabs.length - 1, true);
      this.#tabs[this.#index]?.focus();
    }
  }
}

if (!customElements.get('story-hero')) customElements.define('story-hero', StoryHero);
if (!customElements.get('scroll-manifesto')) customElements.define('scroll-manifesto', ScrollManifesto);
if (!customElements.get('brew-process')) customElements.define('brew-process', BrewProcess);
